import { Download, FileDown, Plus, Save, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Alert, AlertDescription, AlertTitle, Button, CurrencyInput, Input, buttonVariants, cn } from '@payment-plan/ui';
import { EYEBROW_CLASS, SECTION_CARD_CLASS, TOUCH_INPUT_CLASS } from './field-styles';
import type {
  InstallmentType,
  InstallmentsResult,
  IssuedDocument,
  MoneyWire,
  PaymentPlanPreview,
  PlanConditionsInput,
} from '../../../types/separations';
import { mensajeDeError, validacionesDeError } from '../errors';
import { formatCOP, toPesos } from '../money';
import { useGenerarDocumento } from '../use-generar-documento';
import { useSaveInstallments } from '../use-separation-flow';

interface Row {
  key: string;
  fecha: string;
  valor: bigint;
  tipo: InstallmentType;
}

interface InstallmentsStepProps {
  separacionId: string;
  plan: PaymentPlanPreview;
  /** Condiciones del paso 3 con los nombres del motor: las recalcula el back al generar (feat-26). */
  condiciones: PlanConditionsInput;
  /** Si viene, las filas arrancan con estas cuotas (ya cuadradas contra el plan) y no con las del motor. */
  cuotasIniciales?: CuotaInicial[];
  onBack?: () => void;
}

/** Cuota ya guardada con la que puede arrancar el paso (retomar un borrador o una corrección, fix-50). */
export interface CuotaInicial {
  fechaVencimiento: string;
  valor: MoneyWire;
  tipo: InstallmentType;
}

interface ErrorGenerar {
  mensaje: string;
  validaciones: Array<{ code: string; message: string }>;
}

/** Orden cronológico; a igual fecha, la extraordinaria primero (mismo criterio que el motor). */
function ordenar(rows: Row[]): Row[] {
  return [...rows].sort((a, b) => {
    if (a.fecha !== b.fecha) return a.fecha < b.fecha ? -1 : 1;
    if (a.tipo === b.tipo) return 0;
    return a.tipo === 'EXTRAORDINARIA' ? -1 : 1;
  });
}

function mensajeDiferencia(diferencia: bigint): string {
  if (diferencia === 0n) return 'El plan cuadra';
  return diferencia < 0n
    ? `Faltan ${formatCOP(-diferencia)} para cuadrar el plan`
    : `Sobran ${formatCOP(diferencia)} frente al saldo a diferir`;
}

let secuencia = 0;
const nuevaClave = (): string => `extra-${++secuencia}`;

export function InstallmentsStep({
  separacionId,
  plan,
  condiciones,
  cuotasIniciales,
  onBack,
}: InstallmentsStepProps): React.JSX.Element {
  const [rows, setRows] = React.useState<Row[]>(() =>
    cuotasIniciales
      ? cuotasIniciales.map((c, i) => ({
          key: `cuota-${i}`,
          fecha: c.fechaVencimiento.slice(0, 10),
          valor: toPesos(c.valor),
          tipo: c.tipo,
        }))
      : plan.installments.map((c, i) => ({
          key: `cuota-${i}`,
          fecha: c.dueDate.slice(0, 10),
          valor: toPesos(c.value),
          tipo: c.type,
        })),
  );
  const [dirty, setDirty] = React.useState(true);
  const [guardado, setGuardado] = React.useState<InstallmentsResult | null>(null);
  const [extraFecha, setExtraFecha] = React.useState('');
  const [extraValor, setExtraValor] = React.useState(0n);
  const [aviso, setAviso] = React.useState<string | null>(null);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [errorGenerar, setErrorGenerar] = React.useState<ErrorGenerar | null>(null);
  const [emitido, setEmitido] = React.useState<IssuedDocument | null>(null);

  const guardar = useSaveInstallments();
  const generar = useGenerarDocumento();

  const ordenadas = ordenar(rows);
  const saldo = toPesos(plan.balanceToDefer);
  // Suma solo para feedback visual (S-07); la autoridad es `cuadra` del back tras guardar.
  const total = rows.reduce((suma, r) => suma + r.valor, 0n);
  const diferencia = total - saldo;

  const cambiar = (key: string, patch: Partial<Row>): void => {
    setRows((actual) => actual.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setDirty(true);
    setErrorGenerar(null);
  };

  const quitar = (key: string): void => {
    setRows((actual) => actual.filter((r) => r.key !== key));
    setDirty(true);
  };

  const agregarExtraordinaria = (): void => {
    if (!extraFecha || extraValor <= 0n) {
      setAviso('Indica la fecha y el valor de la cuota extraordinaria.');
      return;
    }
    setRows((actual) => [...actual, { key: nuevaClave(), fecha: extraFecha, valor: extraValor, tipo: 'EXTRAORDINARIA' }]);
    setExtraFecha('');
    setExtraValor(0n);
    setAviso(null);
    setDirty(true);
  };

  const guardarCuotas = (): void => {
    setServerError(null);
    if (ordenadas.some((r) => !r.fecha || r.valor <= 0n)) {
      setServerError('Cada cuota debe tener fecha y un valor mayor a cero.');
      return;
    }
    guardar.mutate(
      {
        id: separacionId,
        cuotas: ordenadas.map((r, i) => ({
          numero: i + 1,
          fechaVencimiento: r.fecha,
          valor: r.valor.toString(),
          tipo: r.tipo,
        })),
      },
      {
        onSuccess: (resultado) => {
          setGuardado(resultado);
          setDirty(false);
        },
        onError: (e) => setServerError(mensajeDeError(e, 'guardar las cuotas')),
      },
    );
  };

  let motivo: string | null = null;
  if (emitido) motivo = `El plan de pagos ya fue generado (${emitido.consecutivo}).`;
  else if (dirty || !guardado) motivo = 'Guarda las cuotas para validar el plan.';
  else if (diferencia !== 0n) motivo = `${mensajeDiferencia(diferencia)}.`;
  else if (!guardado.cuadra) {
    motivo = `La suma de cuotas no coincide con el saldo a financiar: diferencia de ${formatCOP(guardado.diferencia)}.`;
  }

  // HU-C05 (feat-26, conectado en fix-50): el back recalcula, valida y emite; aquí solo se muestra.
  const onGenerar = (): void => {
    setErrorGenerar(null);
    generar.mutate(
      { separacionId, condiciones },
      {
        onSuccess: (documento) => setEmitido(documento),
        onError: (e) =>
          setErrorGenerar({
            mensaje: mensajeDeError(e, 'generar el plan de pagos'),
            validaciones: validacionesDeError(e),
          }),
      },
    );
  };

  return (
    <section aria-labelledby="paso-cuotas" className={SECTION_CARD_CLASS}>
      <div>
        <p className={EYEBROW_CLASS}>Paso 4</p>
        <h2 id="paso-cuotas" className="text-xl font-semibold tracking-tight text-foreground">
          Ajuste de cuotas
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ajusta valores o fechas y agrega cuotas extraordinarias. El plan debe cuadrar con el saldo a diferir.
        </p>
      </div>

      <div
        role="status"
        aria-label="Estado del plan"
        aria-live="polite"
        className={cn(
          'flex flex-col gap-1 rounded-lg border p-4 lg:flex-row lg:items-center lg:justify-between',
          diferencia === 0n ? 'border-success/50 bg-success/5' : 'border-warning/60 bg-warning/5',
        )}
      >
        <p className={cn('text-base font-semibold', diferencia === 0n ? 'text-success' : 'text-foreground')}>
          {mensajeDiferencia(diferencia)}
        </p>
        <p className="text-sm tabular-nums text-muted-foreground">
          Total cuotas {formatCOP(total)} · Saldo a diferir {formatCOP(saldo)}
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border/70">
        <table aria-label="Cuotas del plan" className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              {['N.º', 'Vencimiento', 'Valor', ''].map((h, i) => (
                <th key={i} scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {h || <span className="sr-only">Acciones</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((r, i) => {
              const numero = i + 1;
              return (
                <tr key={r.key} className="border-t border-border/60 transition-colors hover:bg-muted/20">
                  <td className="px-3 py-2 align-middle">
                    <span className="block font-semibold tabular-nums">{numero}</span>
                    <span
                      className={cn(
                        'block text-xs',
                        r.tipo === 'EXTRAORDINARIA' ? 'font-medium text-brand-gold-dark' : 'text-muted-foreground',
                      )}
                    >
                      {r.tipo === 'EXTRAORDINARIA' ? 'Extraordinaria' : 'Ordinaria'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="date"
                      aria-label={`Fecha cuota ${numero}`}
                      className={cn(TOUCH_INPUT_CLASS, 'min-w-[9.5rem]')}
                      value={r.fecha}
                      onChange={(e) => cambiar(r.key, { fecha: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <CurrencyInput
                      aria-label={`Valor cuota ${numero}`}
                      className={cn(TOUCH_INPUT_CLASS, 'min-w-[8.5rem]')}
                      value={r.valor}
                      onChange={(v) => cambiar(r.key, { valor: v })}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.tipo === 'EXTRAORDINARIA' && (
                      <Button type="button" variant="ghost" size="icon" aria-label={`Quitar cuota ${numero}`} onClick={() => quitar(r.key)}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <fieldset className="space-y-3 rounded-lg border border-border/70 p-4">
        <legend className="px-1 text-sm font-semibold text-foreground">Cuota extraordinaria</legend>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
          <div className="space-y-1.5">
            <label htmlFor="extra-fecha" className="block text-sm font-medium text-foreground">
              Fecha de la extraordinaria
            </label>
            <Input
              id="extra-fecha"
              type="date"
              className={TOUCH_INPUT_CLASS}
              value={extraFecha}
              onChange={(e) => setExtraFecha(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="extra-valor" className="block text-sm font-medium text-foreground">
              Valor de la extraordinaria
            </label>
            <CurrencyInput id="extra-valor" className={TOUCH_INPUT_CLASS} value={extraValor} onChange={setExtraValor} />
          </div>
          <Button type="button" variant="outline" size="lg" onClick={agregarExtraordinaria}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Agregar extraordinaria
          </Button>
        </div>
        {aviso && (
          <p role="alert" className="text-sm text-destructive">
            {aviso}
          </p>
        )}
      </fieldset>

      {serverError && (
        <p role="alert" className="text-sm text-destructive">
          {serverError}
        </p>
      )}

      {guardado && !dirty && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Cuotas guardadas. {guardado.cuadra ? 'El sistema confirma que el plan cuadra.' : 'El sistema reporta una diferencia.'}
        </p>
      )}

      {errorGenerar && (
        <Alert variant="destructive" role="alert" aria-labelledby="generar-error-titulo">
          <AlertTitle id="generar-error-titulo">{errorGenerar.mensaje}</AlertTitle>
          {errorGenerar.validaciones.length > 0 && (
            <ul className="list-disc space-y-1 pl-4 text-sm">
              {errorGenerar.validaciones.map((v) => (
                <li key={v.code}>{v.message}</li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {emitido && (
        <Alert variant="success" role="status" aria-labelledby="generar-ok-titulo">
          <AlertTitle id="generar-ok-titulo">Plan de pagos generado</AlertTitle>
          <AlertDescription className="space-y-3 text-foreground">
            <p>
              Consecutivo <span className="font-semibold tabular-nums">{emitido.consecutivo}</span>. El enlace de descarga
              vence en 15 minutos.
            </p>
            {emitido.advertencias.length > 0 && (
              <ul className="list-disc pl-4 text-sm text-muted-foreground">
                {emitido.advertencias.map((a) => (
                  <li key={a.code}>{a.message}</li>
                ))}
              </ul>
            )}
            <a href={emitido.descarga.url} download className={cn(buttonVariants({ size: 'lg' }), 'w-full lg:w-auto')}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Descargar PDF
            </a>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col-reverse gap-3 lg:flex-row lg:items-start lg:justify-between">
        {onBack ? (
          <Button type="button" variant="outline" size="lg" onClick={onBack}>
            Volver a condiciones
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-col gap-3 lg:items-end">
          <div className="flex flex-col gap-3 lg:flex-row">
            <Button
              type="button"
              variant="outline"
              size="lg"
              disabled={emitido !== null}
              loading={guardar.isPending}
              onClick={guardarCuotas}
            >
              <Save className="h-4 w-4" aria-hidden="true" />
              Guardar cuotas
            </Button>
            <Button
              type="button"
              size="lg"
              disabled={motivo !== null}
              loading={generar.isPending}
              aria-describedby={motivo ? 'generar-motivo' : undefined}
              onClick={onGenerar}
            >
              <FileDown className="h-4 w-4" aria-hidden="true" />
              Generar plan de pagos
            </Button>
          </div>
          {motivo && (
            <p id="generar-motivo" className="text-sm text-muted-foreground lg:text-right">
              {motivo}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
